import { useState } from 'react'
import clsx from 'clsx'
import { TCGOption } from '../../constants/tcgs'

type Props = {
  tcg: TCGOption
  imgClassName?: string
  fallbackClassName?: string
  // 'initials' (default) renders a compact letter chip (e.g. "DBF") — good
  // when the chip already shows the game name as a caption.
  // 'name' renders the full game name — used when the chip has no caption
  // (the public capture form), so a missing logo still tells the user
  // which game they're tapping.
  fallbackVariant?: 'initials' | 'name'
}

// Renders the TCG's color logo when the asset is present; falls back to text
// when the file 404s so a missing asset doesn't break the layout.
export function TcgLogo({
  tcg,
  imgClassName,
  fallbackClassName,
  fallbackVariant = 'initials',
}: Props) {
  const [errored, setErrored] = useState(false)

  if (errored) {
    if (fallbackVariant === 'name') {
      return (
        <span
          className={clsx(
            'block px-1 text-center font-medium leading-tight text-white/85',
            fallbackClassName ?? 'text-[12px]',
          )}
          aria-label={tcg.name}
        >
          {tcg.name}
        </span>
      )
    }
    return (
      <span
        className={clsx(
          'font-semibold uppercase tracking-wide text-white/75',
          fallbackClassName ?? 'text-[10px]',
        )}
        aria-label={tcg.name}
      >
        {initials(tcg.name)}
      </span>
    )
  }

  return (
    <img
      src={tcg.logo}
      alt={tcg.name}
      onError={() => setErrored(true)}
      draggable={false}
      className={imgClassName}
    />
  )
}

function initials(name: string): string {
  const words = name.split(/[^A-Za-z0-9]+/).filter(Boolean)
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return words.slice(0, 3).map((w) => w[0]).join('').toUpperCase()
}
